import { useEffect, useState, useMemo } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { TreeTable } from 'primereact/treetable';
import { RolService } from '../../service/RolService';

export const MenuRol = ({ id, toast, permss }) => {
    const rolService = useMemo(() => new RolService(), []);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [menus, setMenus] = useState([]);
    // Permisos seleccionados
    const [sconsultar, setSconsultar] = useState([]);
    const [screar, setScrear] = useState([]);
    const [seditar, setSeditar] = useState([]);
    const [seliminar, setSeliminar] = useState([]);
    // Micro-loaders para las celdas de permisos
    const [loadingCells, setLoadingCells] = useState({});
    // Carga inicial y paginación (Solo nodos padre)
    useEffect(() => {
        loadRootNodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, first, rows]);

    const formatNode = (item) => ({
        key: item.id,
        data: { name: item.nombre, fecha: item.fecha },
        leaf: item.ultimo // Indica si se puede expandir o no
    });

    const agregarValoresAlEstado = (items) => {
        setSconsultar(prev => Array.from(new Set([...prev, ...items.filter(i => i.consultar).map(i => i.id)])));
        setScrear(prev => Array.from(new Set([...prev, ...items.filter(i => i.crear).map(i => i.id)])));
        setSeditar(prev => Array.from(new Set([...prev, ...items.filter(i => i.actualizar).map(i => i.id)])));
        setSeliminar(prev => Array.from(new Set([...prev, ...items.filter(i => i.eliminar).map(i => i.id)])));
    };

    // CARGA DE NODOS PRINCIPALES
    const loadRootNodes = () => {
        setLoading(true);
        rolService.getMenusRol({ id: id, padre: 0, total: 0, first: first, rows: rows }).then(res => {
            setTotalRecords(res.data.total);
            setMenus(res.data.menus.map(formatNode));
            agregarValoresAlEstado(res.data.menus);
        }).catch(() => {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error cargando menús' })
        }).finally(() => {
            setLoading(false);
        });
    };

    // EXPANDIR NODO (Petición aislada, no altera effect)
    const onExpand = (event) => {
        const nodeKey = event.node.key;
        setLoading(true);

        rolService.getMenusRol({ id: id, padre: nodeKey, total: totalRecords, first: 0, rows: 0 }).then(res => {
            const newChildren = res.data.menus.map(formatNode);
            agregarValoresAlEstado(res.data.menus);
            // Función recursiva para actualizar el árbol preservando el estado previo
            setMenus(prevMenus => {
                const updateTree = (nodes) => {
                    return nodes.map(n => {
                        if (n.key === nodeKey) {
                            return { ...n, children: newChildren };
                        }
                        if (n.children) {
                            return { ...n, children: updateTree(n.children) };
                        }
                        return n;
                    });
                };
                return updateTree(prevMenus);
            });
        }).catch(() => {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error cargando submenús' })
        }).finally(() => {
            setLoading(false);
        });
    };

    const onPage = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    // ASIGNAR PERMISOS (Con Micro-Spinners)
    const asignar = (checked, nodeId, lista, setLista, opcion) => {
        const cellKey = `${nodeId}_${opcion}`;
        setLoadingCells(prev => ({ ...prev, [cellKey]: true }));
        // Optimistic UI Update
        if (checked) {
            setLista(prev => [...prev, nodeId]);
        } else {
            setLista(prev => prev.filter(key => key !== nodeId));
        }

        rolService.putMenu({ id: nodeId, rol: id, agregar: checked, opcion: opcion }).then(res => {
            toast.current?.show({ severity: 'success', summary: 'Exitoso', detail: res.data?.data || 'Permiso actualizado', life: 3000 });
        }).catch(error => {
            // Revertir en caso de fallo
            if (checked) {
                setLista(prev => prev.filter(key => key !== nodeId));
            } else {
                setLista(prev => [...prev, nodeId]);
            }
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error actualizando permiso', life: 3000 });
        }).finally(() => {
            setLoadingCells(prev => ({ ...prev, [cellKey]: false }));
        });
    };

    const renderCellCheckbox = (nodeData, lista, setLista, opcion) => {
        const nodeId = nodeData.key;
        const cellKey = `${nodeId}_${opcion}`;
        const isProcessing = loadingCells[cellKey] === true;
        const isChecked = lista.includes(nodeId);

        return (
            <div className="flex justify-content-center align-items-center" style={{ height: '2rem' }}>
                {isProcessing ? (
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.3rem', color: 'var(--primary-color)' }}></i>
                ) : (
                    <Checkbox checked={isChecked} disabled={!permss?.actualizar} onChange={e => asignar(e.checked, nodeId, lista, setLista, opcion)} />
                )}
            </div>
        );
    };

    return (
        <>
            <Message severity="warn" text="Seleccione tanto el menú principal como los submenús requeridos." className="mb-3 w-full" />
            <TreeTable value={menus} lazy paginator totalRecords={totalRecords} first={first} rows={rows} onPage={onPage}
                onExpand={onExpand} loading={loading} size="small" emptyMessage="No se encontraron menús.">
                <Column field="name" header="Menú" expander style={{ width: '35%' }} />
                <Column header="Consultar" body={node => renderCellCheckbox(node, sconsultar, setSconsultar, 0)} style={{ width: '10%', textAlign: 'center' }} />
                <Column header="Crear" body={node => renderCellCheckbox(node, screar, setScrear, 1)} style={{ width: '10%', textAlign: 'center' }} />
                <Column header="Editar" body={node => renderCellCheckbox(node, seditar, setSeditar, 2)} style={{ width: '10%', textAlign: 'center' }} />
                <Column header="Eliminar" body={node => renderCellCheckbox(node, seliminar, setSeliminar, 3)} style={{ width: '10%', textAlign: 'center' }} />
                <Column field="fecha" header="Fecha Asignación" style={{ width: '15%', textAlign: 'center' }} />
            </TreeTable>
        </>
    );
};